import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import LocaleSelector from '../localeSelector';
import type { NavTab } from './nav-tab';
import { RoleAssumptionDropdown } from './role-assumption-dropdown';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  tabs: NavTab[];
  selectedTab: string;
  setSelectedTab: (tab: string) => void;
  adminOnlyTabs: NavTab[];
  adminOrMaintainerTabs: NavTab[];
  onSignOut: () => void;
};

const itemBase =
  'block w-full text-left px-300 py-200 text-body-md text-core-slate-1300 hover:bg-core-slate-50 transition-colors';

const renderMenuLink = (
  tab: NavTab,
  t: (k: string) => string,
  selectedTab: string,
  onSelect: (to: string) => void,
  onClose: () => void
) => {
  const isSelected = selectedTab === tab.to;
  return (
    <Link
      key={tab.to}
      to={`/${tab.to}`}
      onClick={() => {
        onSelect(tab.to);
        onClose();
      }}
      className={`${itemBase} ${isSelected ? 'font-bold bg-core-slate-50' : ''}`}
    >
      {t(`navbar.display.${tab.label}`)}
    </Link>
  );
};

export const MobileNavDrawer = ({
  isOpen,
  onClose,
  tabs,
  selectedTab,
  setSelectedTab,
  adminOnlyTabs,
  adminOrMaintainerTabs,
  onSignOut,
}: Props) => {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Click outside to close.
  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (menuRef.current?.contains(target)) return;
      // Ignore the hamburger button itself (it toggles).
      const burger = document.getElementById('navbar-mobile-burger');
      if (burger?.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="xl:hidden absolute right-300 top-full mt-150 z-[100] w-[260px] max-w-[calc(100vw-24px)] bg-core-white border-10 border-core-blue-900 rounded-100 shadow-xl overflow-hidden"
    >
      <nav aria-label="Primary navigation" className="flex flex-col py-100">
        {tabs.map((tab) => renderMenuLink(tab, t, selectedTab, setSelectedTab, onClose))}
        {adminOnlyTabs.map((tab) => renderMenuLink(tab, t, selectedTab, setSelectedTab, onClose))}
        {adminOrMaintainerTabs.map((tab) =>
          renderMenuLink(tab, t, selectedTab, setSelectedTab, onClose)
        )}

        <div className="border-t-10 border-core-slate-100 my-100" />
        <RoleAssumptionDropdown variant="light" className="px-300 py-200" />
        <Link
          target="_blank"
          to="https://baseplate.legogroup.io/docs/default/component/labeling-platform"
          onClick={onClose}
          className={itemBase}
        >
          {t('navbar.display.documentation')}
        </Link>

        <div className="flex items-center justify-between px-300 py-200">
          <span className="text-body-md text-core-slate-1300">{t('navbar.display.language')}</span>
          <div className="text-core-slate-1300">
            <LocaleSelector variant="light" />
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            onClose();
            onSignOut();
          }}
          className={itemBase}
        >
          {t('navbar.display.signOut')}
        </button>
      </nav>
    </div>
  );
};
